/**
 * Procedural Web Audio engine — no external samples required.
 * Generates music, impacts, merges, whooshes, UI clicks.
 */

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxGain!: GainNode;
  private musicGain!: GainNode;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private enabled = true;
  private musicOn = true;
  private sfxOn = true;
  private masterVolume = 0.7;
  private dangerOsc: OscillatorNode | null = null;
  private dangerGain: GainNode | null = null;
  private started = false;

  async unlock(): Promise<void> {
    if (this.started && this.ctx?.state === 'running') return;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.masterVolume;
      this.master.connect(this.ctx.destination);
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.85;
      this.sfxGain.connect(this.master);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.28;
      this.musicGain.connect(this.master);
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.started = true;
    if (this.musicOn) this.startMusic();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (this.master) this.master.gain.value = on ? this.masterVolume : 0;
  }

  setMusic(on: boolean): void {
    this.musicOn = on;
    if (!this.ctx) return;
    if (on) this.startMusic();
    else this.stopMusic();
  }

  setSfx(on: boolean): void {
    this.sfxOn = on;
  }

  setVolume(v: number): void {
    this.masterVolume = v;
    if (this.master && this.enabled) this.master.gain.value = v;
  }

  private now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType,
    gain: number,
    dest: AudioNode,
    slideTo?: number,
  ): void {
    if (!this.ctx || !this.sfxOn || !this.enabled) return;
    const t = this.now();
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t + dur);
    }
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noiseBurst(dur: number, gain: number, bpFreq: number): void {
    if (!this.ctx || !this.sfxOn || !this.enabled) return;
    const t = this.now();
    const len = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = bpFreq;
    bp.Q.value = 1.2;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(this.sfxGain);
    src.start(t);
    src.stop(t + dur);
  }

  playDrop(): void {
    this.tone(420, 0.08, 'sine', 0.12, this.sfxGain, 280);
    this.noiseBurst(0.06, 0.08, 800);
  }

  playImpact(intensity: number, pitch = 1): void {
    const f = (180 + intensity * 80) * pitch;
    this.tone(f, 0.07 + intensity * 0.04, 'triangle', 0.08 + intensity * 0.1, this.sfxGain, f * 0.5);
    this.noiseBurst(0.05 + intensity * 0.03, 0.06 + intensity * 0.08, 400 + intensity * 200);
  }

  playMerge(tier: number, combo: number): void {
    if (!this.ctx || !this.sfxOn || !this.enabled) return;
    const base = 320 * Math.pow(1.08, tier) * (1 + combo * 0.02);
    const t = this.now();
    // Arpeggio pop
    const notes = [1, 1.25, 1.5, 2];
    notes.forEach((n, i) => {
      const osc = this.ctx!.createOscillator();
      const g = this.ctx!.createGain();
      osc.type = i % 2 === 0 ? 'sine' : 'triangle';
      osc.frequency.value = base * n;
      const start = t + i * 0.045;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.14 - i * 0.02, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(g);
      g.connect(this.sfxGain);
      osc.start(start);
      osc.stop(start + 0.25);
    });
    this.noiseBurst(0.1, 0.1 + tier * 0.01, 1200);
    if (tier >= 7) {
      this.tone(base * 0.5, 0.4, 'sine', 0.1, this.sfxGain, base);
    }
  }

  playWhoosh(): void {
    this.noiseBurst(0.12, 0.07, 600);
    this.tone(200, 0.12, 'sine', 0.05, this.sfxGain, 90);
  }

  playUIClick(): void {
    this.tone(660, 0.05, 'sine', 0.1, this.sfxGain, 880);
  }

  playUIConfirm(): void {
    this.tone(520, 0.08, 'triangle', 0.1, this.sfxGain);
    this.tone(780, 0.12, 'sine', 0.08, this.sfxGain);
  }

  playGameOver(): void {
    if (!this.ctx || !this.sfxOn) return;
    const notes = [400, 350, 300, 220];
    notes.forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.25, 'triangle', 0.12, this.sfxGain, f * 0.8), i * 120);
    });
  }

  playWinFanfare(): void {
    if (!this.ctx || !this.sfxOn) return;
    const notes = [523, 659, 784, 1046];
    notes.forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.3, 'sine', 0.12, this.sfxGain), i * 100);
    });
  }

  setDanger(level: number): void {
    // level 0..1
    if (!this.ctx || !this.sfxOn || !this.enabled) {
      this.stopDanger();
      return;
    }
    if (level <= 0) {
      this.stopDanger();
      return;
    }
    if (!this.dangerOsc) {
      this.dangerOsc = this.ctx.createOscillator();
      this.dangerGain = this.ctx.createGain();
      this.dangerOsc.type = 'sine';
      this.dangerOsc.frequency.value = 110;
      this.dangerGain.gain.value = 0;
      this.dangerOsc.connect(this.dangerGain);
      this.dangerGain.connect(this.sfxGain);
      this.dangerOsc.start();
    }
    this.dangerOsc.frequency.setTargetAtTime(110 + level * 80, this.now(), 0.1);
    this.dangerGain!.gain.setTargetAtTime(0.04 * level, this.now(), 0.1);
  }

  private stopDanger(): void {
    if (this.dangerGain) {
      this.dangerGain.gain.setTargetAtTime(0, this.now(), 0.05);
    }
  }

  private startMusic(): void {
    this.stopMusic();
    if (!this.ctx || !this.musicOn || !this.enabled) return;
    // Simple generative pentatonic loop
    const scale = [262, 294, 330, 392, 440, 523, 587];
    const bpm = 92;
    const stepDur = 60 / bpm / 2;

    const tick = () => {
      if (!this.ctx || !this.musicOn) return;
      const t = this.now();
      const step = this.musicStep % 16;
      // Bass every 4
      if (step % 4 === 0) {
        const f = scale[0]! / 2;
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.09, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + stepDur * 1.8);
        osc.connect(g);
        g.connect(this.musicGain);
        osc.start(t);
        osc.stop(t + stepDur * 2);
      }
      // Melody sparse
      if (step % 2 === 0 && Math.random() > 0.35) {
        const f = scale[Math.floor(Math.random() * scale.length)]!;
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.05, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + stepDur * 1.2);
        osc.connect(g);
        g.connect(this.musicGain);
        osc.start(t);
        osc.stop(t + stepDur * 1.5);
      }
      // Soft pad chord on bar
      if (step === 0) {
        [scale[0]!, scale[2]!, scale[4]!].forEach((f) => {
          const osc = this.ctx!.createOscillator();
          const g = this.ctx!.createGain();
          osc.type = 'sine';
          osc.frequency.value = f / 2;
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.025, t + 0.3);
          g.gain.exponentialRampToValueAtTime(0.0001, t + stepDur * 8);
          osc.connect(g);
          g.connect(this.musicGain);
          osc.start(t);
          osc.stop(t + stepDur * 8);
        });
      }
      this.musicStep++;
      this.musicTimer = window.setTimeout(tick, stepDur * 1000);
    };
    tick();
  }

  private stopMusic(): void {
    if (this.musicTimer !== null) {
      clearTimeout(this.musicTimer);
      this.musicTimer = null;
    }
  }

  dispose(): void {
    this.stopMusic();
    this.stopDanger();
    if (this.dangerOsc) {
      try {
        this.dangerOsc.stop();
      } catch {
        /* ignore */
      }
      this.dangerOsc = null;
    }
    void this.ctx?.close();
    this.ctx = null;
  }
}
